// src/contexts/LanguageContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

const LanguageContext = createContext()

const STORAGE_KEY = 'mojo_lang'

// Dictionary
const translations = {
    en: {
        // ---------------- NAV ----------------
        'nav.dashboard': 'Dashboard',
        'nav.send': 'Send Money',
        'nav.wallet': 'Wallet',
        'nav.transactions': 'Transactions',
        'nav.login': 'Login',
        'nav.signup': 'Sign Up',

        'nav.profile': 'Profile',
        'nav.settings': 'Settings',
        'nav.logout': 'Logout',
        'nav.account': 'Account',

        // ---------------- COMMON ----------------
        'common.all': 'All',
        'common.sent': 'Sent',
        'common.received': 'Received',
        'common.pending': 'Pending',
        'common.close': 'Close',
        'common.cancel': 'Cancel',
        'common.add': 'Add',
        'common.save': 'Save',
        'common.download': 'Download',
        'common.viewAll': 'View All',
        'common.back': 'Back',

        // Countries (for dropdowns)
        'country.US': 'United States',
        'country.UK': 'United Kingdom',
        'country.CA': 'Canada',
        'country.AU': 'Australia',
        'country.FR': 'France',
        'country.SL': 'Sierra Leone',
        'country.LR': 'Liberia',
        'country.GN': 'Guinea',

        // Currencies (labels if you choose to use them)
        'currency.USD': 'US Dollar',
        'currency.GBP': 'British Pound',
        'currency.CAD': 'Canadian Dollar',
        'currency.AUD': 'Australian Dollar',
        'currency.EUR': 'Euro',
        'currency.SLE': 'Sierra Leonean Leone',
        'currency.LRD': 'Liberian Dollar',
        'currency.GNF': 'Guinean Franc',

        // Status labels
        'status.completed': 'Completed',
        'status.pending': 'Pending',
        'status.failed': 'Failed',

        // ---------------- HOME ----------------
        'home.brand': 'MojoPayr',

        'home.hero.title': 'One smart wallet for the U.S., U.K., Canada, Australia & France',
        'home.hero.subtitle':
            'Move money to mobile wallets and banks in Sierra Leone, Liberia, and Guinea in minutes, not days.',

        'home.hero.typed1': 'Send to Mobile Money',
        'home.hero.typed2': 'Send to Bank Accounts',
        'home.hero.typed3': 'Pay Goods & Services',
        'home.hero.typed4': 'Donations and Contributions',

        'home.hero.ctaPrimary': 'Get started',
        'home.hero.ctaSecondary': 'See features',

        'home.chip.kyc': 'ID verification (KYC)',
        'home.chip.fast': 'Fast delivery',
        'home.chip.lowFees': 'Low fees goal',
        'home.chip.alerts': 'SMS + Email alerts',

        'home.liveNow': 'Live now',
        'home.destinations': 'Mano River destinations',

        'home.scope.title': 'What you will support (scope)',
        'home.scope.sendMoney': 'Send money (Visa / Wallet)',
        'home.scope.deliverMobile': 'Deliver to Mobile Money',
        'home.scope.deliverBank': 'Deliver to Bank Accounts',
        'home.scope.payServices': 'Pay goods & services',
        'home.scope.donations': 'Donations',
        'home.scope.contributions': 'Contributions',
        'home.scope.kyc': 'ID verification (KYC)',
        'home.scope.notifications': 'Notifications',
        'home.scope.comingSoon': 'Coming soon',
        'home.scope.yes': 'Yes',

        'home.section.features.title': 'What you can do',
        'home.section.features.subtitle':
            'Send from the diaspora to Mano River countries. Deliver to mobile money and banks. Now you can also pay for goods & services.',

        'home.tag.live': 'Live',
        'home.action.useNow': 'Use now',

        'home.feature.wallet.title': 'Multi-currency wallet',
        'home.feature.wallet.desc': 'Hold balances and send in minutes. USD, GBP, EUR, AUD (phase 1).',

        'home.feature.mobile.title': 'Send to mobile money',
        'home.feature.mobile.desc': 'Deliver straight to mobile wallets in Sierra Leone, Liberia, and Guinea.',

        'home.feature.bank.title': 'Send to bank accounts',
        'home.feature.bank.desc': 'Send direct to bank accounts in supported countries.',

        'home.feature.pay.title': 'Pay for goods & services',
        'home.feature.pay.desc': 'Pay schools, vendors, and service providers back home from the diaspora.',

        'home.feature.donations.title': 'Donations',
        'home.feature.donations.desc': 'Donate to verified causes and track receipts and impact.',

        'home.feature.contrib.title': 'Contributions',
        'home.feature.contrib.desc': 'Run group contributions for families, associations, events, and projects.',

        'home.cta.title': 'Start your first transfer',
        'home.cta.subtitle':
            'Send from the U.S., U.K., Canada, Australia, or France. Deliver to mobile money or bank accounts in Sierra Leone, Liberia, and Guinea.',
        'home.cta.payServices': 'Pay services',

        'home.testimonials.title': 'Testimonials',
        'home.testimonials.subtitle': 'Real feedback from early users sending money back home.',

        'home.testimonial.1.quote': 'Fast and clear. My family got the mobile money the same day.',
        'home.testimonial.1.meta': 'U.S. to Sierra Leone',

        'home.testimonial.2.quote': 'Simple flow. I like that I can track the transfer and receipts.',
        'home.testimonial.2.meta': 'U.K. to Liberia',

        'home.testimonial.3.quote': 'The app feels clean. Fees are easy to understand.',
        'home.testimonial.3.meta': 'France to Guinea',

        // ---------------- FOOTER ----------------
        'footer.about':
            'Fast, secure, and affordable money transfers to Sierra Leone, Liberia, and Guinea. Connecting families and businesses across borders.',

        'footer.section.services': 'Services',
        'footer.section.support': 'Support',
        'footer.section.company': 'Company',
        'footer.section.legal': 'Legal',

        'footer.link.send': 'Send Money',
        'footer.link.receive': 'Receive Money',
        'footer.link.mobile': 'Mobile Money',
        'footer.link.bank': 'Bank Transfers',

        'footer.link.help': 'Help Center',
        'footer.link.contact': 'Contact Us',
        'footer.link.faq': 'FAQ',
        'footer.link.status': 'Status',

        'footer.link.about': 'About Us',
        'footer.link.careers': 'Careers',
        'footer.link.press': 'Press',
        'footer.link.blog': 'Blog',

        'footer.link.privacy': 'Privacy Policy',
        'footer.link.terms': 'Terms of Service',
        'footer.link.compliance': 'Compliance',
        'footer.link.kyc': 'KYC Policy',

        'footer.rights': 'All rights reserved.',
        'footer.licensed': 'Licensed Money Transmitter',
        'footer.nmls': 'NMLS ID: 1234567',
        'footer.regulatory': 'Regulatory Information',

        // ---------------- DASHBOARD ----------------
        'dashboard.welcome': 'Welcome back, {{name}}!',
        'dashboard.subtitle': 'Your account at a glance.',

        'dashboard.stats.totalBalance': 'Total Balance',
        'dashboard.stats.totalSent': 'Total Sent',
        'dashboard.stats.totalReceived': 'Total Received',
        'dashboard.stats.transactions': 'Transactions',

        'dashboard.quickActions': 'Quick Actions',
        'dashboard.transferHistory': 'Transfer History',
        'dashboard.viewAll': 'View All',
        'dashboard.recentTransactions': 'Recent Transactions',

        'dashboard.action.send.title': 'Send money',
        'dashboard.action.send.desc': 'Send to mobile money or bank in minutes.',

        'dashboard.action.wallet.title': 'Wallet',
        'dashboard.action.wallet.desc': 'Add money and manage your balances.',

        'dashboard.action.pay.title': 'Pay goods & services',
        'dashboard.action.pay.desc': 'Pay schools, vendors, and services back home.',

        'dashboard.action.history.title': 'History',
        'dashboard.action.history.desc': 'View transfers and receipts.',

        'dashboard.quick.payment': 'Payment',
        'dashboard.quick.bank': 'Bank',
        'dashboard.quick.payBill': 'Pay Bill',
        'dashboard.quick.donate': 'Donate',
        'dashboard.quick.tickets': 'Tickets',
        'dashboard.quick.contribution': 'Contribution/Osusu',
        'dashboard.quick.proofCenter': 'Proof Center',
        'dashboard.quick.servicePros': 'Service Pros',
        'dashboard.quickActions.label': 'Quick actions',
        'dashboard.walletBalance.label': 'Wallet balance',
        'dashboard.walletBalance.today': '+$120 today',
        'dashboard.weeklyTransfers.label': 'Weekly transfers',
        'dashboard.sampleData.label': 'Sample data for layout only',
        'dashboard.recentTransactions.label': 'Recent transactions',

        // ---------------- SEND MONEY ----------------
        'send.title': 'Send Money',
        'send.subtitle':
            'Pay with Visa/card or wallet balance. Deliver to mobile money or bank accounts in Sierra Leone, Liberia, and Guinea.',

        'send.sendFrom': 'Send from',
        'send.sendTo': 'Send to',

        'send.payWith': 'Pay with',
        'send.pay.card': 'Visa / Card',
        'send.pay.wallet': 'Wallet balance',

        'send.chip.secure': 'Secure payments',
        'send.chip.lowFees': 'Low fees',
        'send.chip.fast': 'Fast delivery',

        'send.deliverTo': 'Deliver to',
        'send.deliver.mobile': 'Mobile money',
        'send.deliver.bank': 'Bank account',

        'send.amount': 'Amount',
        'send.recipientName': 'Recipient name',
        'send.recipientPhone': 'Recipient mobile money number',
        'send.bankName': 'Bank name',
        'send.accountNumber': 'Account number',

        'send.saveRecipient': 'Save recipient',
        'send.continue': 'Continue',

        'send.summary.title': 'Transfer summary',
        'send.summary.payWith': 'Pay with',
        'send.summary.deliverTo': 'Deliver to',
        'send.summary.amount': 'Amount',
        'send.summary.fee': 'Transfer fee',
        'send.summary.total': 'Total you pay',
        'send.summary.rate': 'Rate',
        'send.summary.receiverGets': 'Receiver gets (est.)',
        'send.summary.estimatesOnly':
            'Estimates only. Final amounts depend on payout partner rates and compliance checks.',

        'send.phone.dialCode': 'Dial code',
        'send.phone.supportedPrefixes': 'Supported prefixes',
        'send.phone.supportInfo': 'We only support some mobile money operators right now.',
        'send.phone.supportInfo2': 'If your number is not supported, you will see a message.',
        'send.phone.notSupported': 'We are not sending to that operator yet',

        'send.validation.amount': 'Enter an amount',
        'send.validation.name': 'Enter recipient name',
        'send.validation.phone': 'Enter recipient phone number',
        'send.validation.operator': 'We are not sending to that operator yet',
        'send.validation.bankName': 'Enter bank name',
        'send.validation.bankAccount': 'Enter bank account number',

        'send.confirm.title': 'Confirm transfer',
        'send.confirm.recipient': 'Recipient',
        'send.confirm.phone': 'Mobile number',
        'send.confirm.bank': 'Bank',
        'send.confirm.account': 'Account',
        'send.confirm.details': 'Details',
        'send.confirm.confirmBtn': 'Confirm',
        'send.confirm.sending': 'Sending...',

        'send.success': 'Transfer created. Next step: payment processing.',

        // ---------------- WALLET ----------------
        'wallet.title': 'My Wallet',
        'wallet.subtitle': 'Manage your balances and track your transactions',
        'wallet.availableBalance': 'Available Balance',

        'wallet.baseWallet': 'Base wallet: {{currency}}',
        'wallet.addCurrency': 'Add currency',

        'wallet.addMoney': 'Add Money',
        'wallet.withdraw': 'Withdraw',
        'wallet.convert': 'Convert Currency',

        'wallet.recentTransactions': 'Recent Transactions',
        'wallet.tabs.all': 'All',
        'wallet.tabs.sent': 'Sent',
        'wallet.tabs.received': 'Received',
        'wallet.tabs.pending': 'Pending',

        'wallet.monthlyActivity': 'Monthly Activity',
        'wallet.quickStats': 'Quick Stats',
        'wallet.totalSent30': 'Total Sent (30 days)',
        'wallet.totalReceived30': 'Total Received (30 days)',
        'wallet.activeTransactions': 'Active Transactions',

        // Wallet dialogs
        'wallet.dialog.addCurrency.title': 'Add a currency',
        'wallet.dialog.addCurrency.currency': 'Currency',
        'wallet.dialog.addCurrency.helper': 'Your base wallet stays {{base}}. You can add extra wallets anytime.',
        'wallet.dialog.addCurrency.cancel': 'Cancel',
        'wallet.dialog.addCurrency.add': 'Add',

        'wallet.dialog.deposit.title': 'Add Money to Wallet',
        'wallet.dialog.deposit.amount': 'Amount ({{currency}})',
        'wallet.dialog.deposit.method': 'Payment Method',
        'wallet.dialog.deposit.method.card': 'Debit/Credit Card',
        'wallet.dialog.deposit.method.bank': 'Bank Transfer',
        'wallet.dialog.deposit.method.mobile': 'Mobile Money',
        'wallet.dialog.deposit.cancel': 'Cancel',
        'wallet.dialog.deposit.confirm': 'Add Money',

        'wallet.dialog.withdraw.title': 'Withdraw from Wallet',
        'wallet.dialog.withdraw.amount': 'Amount ({{currency}})',
        'wallet.dialog.withdraw.to': 'Withdraw To',
        'wallet.dialog.withdraw.to.bank': 'Bank Account',
        'wallet.dialog.withdraw.to.mobile': 'Mobile Money',
        'wallet.dialog.withdraw.cancel': 'Cancel',
        'wallet.dialog.withdraw.confirm': 'Withdraw',

        // ---------------- TRANSACTIONS ----------------
        'transactions.title': 'Transaction History',
        'transactions.subtitle.withEmail': 'Track and manage transactions for {{email}}',
        'transactions.subtitle.noEmail': 'Track and manage all your financial activities',

        'transactions.stats.totalSent': 'Total Sent',
        'transactions.stats.totalReceived': 'Total Received',
        'transactions.stats.pending': 'Pending',
        'transactions.stats.totalFees': 'Total Fees',
        'transactions.stats.oneTransaction': '1 transaction',
        'transactions.stats.averagePct': 'Average {{pct}}',

        'transactions.monthlyActivity': 'Monthly Activity',

        'transactions.search.placeholder': 'Search transactions...',
        'transactions.filters.status': 'Status',
        'transactions.filters.type': 'Type',
        'transactions.filters.dateRange': 'Date Range',

        'transactions.filters.status.all': 'All Status',
        'transactions.filters.status.completed': 'Completed',
        'transactions.filters.status.pending': 'Pending',
        'transactions.filters.status.failed': 'Failed',

        'transactions.filters.type.all': 'All Types',
        'transactions.filters.type.send': 'Sent',
        'transactions.filters.type.receive': 'Received',
        'transactions.filters.type.conversion': 'Conversion',

        'transactions.filters.date.all': 'All Time',
        'transactions.filters.date.today': 'Today',
        'transactions.filters.date.week': 'This Week',
        'transactions.filters.date.month': 'This Month',

        'transactions.showing': 'Showing {{shown}} of {{total}} transactions',

        // Table headers
        'transactions.table.id': 'Transaction ID',
        'transactions.table.type': 'Type',
        'transactions.table.description': 'Description',
        'transactions.table.amount': 'Amount',
        'transactions.table.date': 'Date',
        'transactions.table.status': 'Status',
        'transactions.table.actions': 'Actions',
        'transactions.table.fee': 'Fee: {{fee}}',

        'transactions.export': 'Export Transactions',
        'transactions.export.soon': 'Export functionality coming soon!',

        // Receipt modal
        'transactions.receipt.title': 'Receipt',
        'transactions.receipt.receiptId': 'Receipt ID',
        'transactions.receipt.date': 'Date',
        'transactions.receipt.status': 'Status',
        'transactions.receipt.method': 'Method',
        'transactions.receipt.amount': 'Amount',
        'transactions.receipt.fee': 'Fee',
        'transactions.receipt.recipient': 'Recipient',
        'transactions.receipt.close': 'Close',
        'transactions.receipt.download': 'Download',

        // ---------------- INVOICES ----------------
        'invoices.title': 'Invoices',
        'invoices.subtitle': 'Create and track invoices for your business.',
        'invoices.create': 'Create Invoice',
        'invoices.onlyBusiness': 'Invoices are only available for business accounts.',
        'invoices.loading': 'Loading invoices...',
        'invoices.emptyTitle': 'No invoices yet.',
        'invoices.emptySubtitle': 'Click "Create Invoice" to make your first one.',
        'invoices.table.invoice': 'Invoice',
        'invoices.table.customer': 'Customer',
        'invoices.table.total': 'Total',
        'invoices.table.due': 'Due',
        'invoices.table.status': 'Status',
        'invoices.table.actions': 'Actions',
        'invoices.actions.copy': 'Copy payment link',
        'invoices.actions.open': 'Open payment link',
        'invoices.actions.copied': 'Copied',
        'invoices.actions.noLink': 'No link',
        'invoices.errors.loadFailed': 'Failed to load invoices.',
        'invoices.status.paid': 'Paid',
        'invoices.status.sent': 'Sent',
        'invoices.status.overdue': 'Overdue',
        'invoices.status.draft': 'Draft',

        // ---------------- CREATE INVOICE ----------------
        'invoiceCreate.title': 'Create Invoice',
        'invoiceCreate.subtitle': 'Send a payment link to your customer.',
        'invoiceCreate.onlyBusiness': 'Only business accounts can create invoices.',
        'invoiceCreate.customerName': 'Customer name',
        'invoiceCreate.customerEmail': 'Customer email',
        'invoiceCreate.customerPhone': 'Customer phone',
        'invoiceCreate.currency': 'Currency',
        'invoiceCreate.locked': 'Locked',
        'invoiceCreate.currencyLockedHelp': 'Currency is locked to your business country.',
        'invoiceCreate.dueDate': 'Due date',
        'invoiceCreate.items': 'Items',
        'invoiceCreate.description': 'Description',
        'invoiceCreate.qty': 'Qty',
        'invoiceCreate.unitPrice': 'Unit price',
        'invoiceCreate.remove': 'Remove',
        'invoiceCreate.addItem': 'Add item',
        'invoiceCreate.note': 'Note (optional)',
        'invoiceCreate.total': 'Total',
        'invoiceCreate.create': 'Create invoice',
        'invoiceCreate.creating': 'Creating...',
        'invoiceCreate.errors.customerNameRequired': 'Customer name is required.',
        'invoiceCreate.errors.dueDateRequired': 'Due date is required.',
        'invoiceCreate.errors.addItemAmount': 'Add at least one item with an amount.',
        'invoiceCreate.errors.createFailed': 'Failed to create invoice.',

        // ---------------- PUBLIC INVOICE PAY (PAYER) ----------------
        'publicInvoice.title': 'Pay Invoice',
        'publicInvoice.subtitle': 'Review the invoice details and pay securely.',
        'publicInvoice.loading': 'Loading invoice...',
        'publicInvoice.notFound': 'Invoice not found.',
        'publicInvoice.goHome': 'Go Home',

        'publicInvoice.invoiceLabel': 'Invoice',
        'publicInvoice.customerLabel': 'Customer',
        'publicInvoice.dueLabel': 'Due',

        'publicInvoice.amountDue': 'Amount due',
        'publicInvoice.youPay': 'You pay',
        'publicInvoice.yourCurrency': 'Your currency',

        'publicInvoice.exchangeRate': 'Exchange rate',
        'publicInvoice.feeEstimate': 'Fee estimate',
        'publicInvoice.total': 'Total',
        'publicInvoice.theyReceive': 'They receive',

        'publicInvoice.items': 'Items',
        'publicInvoice.itemFallback': 'Item',
        'publicInvoice.note': 'Note',

        'publicInvoice.back': 'Back',
        'publicInvoice.startingPayment': 'Starting payment...',
        'publicInvoice.payNow': 'Pay now',
        'publicInvoice.missingPayUrl': 'Payment setup is missing (no pay_url returned).',
        'publicInvoice.startPayFailed': 'Failed to start payment.',
    },

    fr: {
        // ---------------- NAV ----------------
        'nav.dashboard': 'Tableau de bord',
        'nav.send': 'Envoyer',
        'nav.wallet': 'Portefeuille',
        'nav.transactions': 'Transactions',
        'nav.login': 'Connexion',
        'nav.signup': "S'inscrire",

        'nav.profile': 'Profil',
        'nav.settings': 'Parametres',
        'nav.logout': 'Deconnexion',
        'nav.account': 'Compte',

        // ---------------- COMMON ----------------
        'common.all': 'Tout',
        'common.sent': 'Envoye',
        'common.received': 'Recu',
        'common.pending': 'En attente',
        'common.close': 'Fermer',
        'common.cancel': 'Annuler',
        'common.add': 'Ajouter',
        'common.save': 'Enregistrer',
        'common.download': 'Telecharger',
        'common.viewAll': 'Voir tout',
        'common.back': 'Retour',

        // Countries (for dropdowns)
        'country.US': 'Etats-Unis',
        'country.UK': 'Royaume-Uni',
        'country.CA': 'Canada',
        'country.AU': 'Australie',
        'country.FR': 'France',
        'country.SL': 'Sierra Leone',
        'country.LR': 'Liberia',
        'country.GN': 'Guinee',

        // Currencies
        'currency.USD': 'Dollar americain',
        'currency.GBP': 'Livre sterling',
        'currency.CAD': 'Dollar canadien',
        'currency.AUD': 'Dollar australien',
        'currency.EUR': 'Euro',
        'currency.SLE': 'Leone sierra-leonais',
        'currency.LRD': 'Dollar liberien',
        'currency.GNF': 'Franc guineen',

        // Status labels
        'status.completed': 'Termine',
        'status.pending': 'En attente',
        'status.failed': 'Echec',

        // ---------------- HOME ----------------
        'home.brand': 'MojoPayr',

        'home.hero.title': 'Un portefeuille pour les Etats-Unis, le Royaume-Uni, le Canada, l Australie et la France',
        'home.hero.subtitle':
            'Envoyez de l argent vers les portefeuilles mobiles et les banques en Sierra Leone, au Liberia et en Guinee en quelques minutes, pas en quelques jours.',

        'home.hero.typed1': 'Envoyer vers Mobile Money',
        'home.hero.typed2': 'Envoyer vers les comptes bancaires',
        'home.hero.typed3': 'Payer des biens et services',
        'home.hero.typed4': 'Dons et contributions',

        'home.hero.ctaPrimary': 'Commencer',
        'home.hero.ctaSecondary': 'Voir les fonctionnalites',

        'home.chip.kyc': 'Verification d identite (KYC)',
        'home.chip.fast': 'Livraison rapide',
        'home.chip.lowFees': 'Objectif de frais bas',
        'home.chip.alerts': 'Alertes SMS + e-mail',

        'home.liveNow': 'Disponible',
        'home.destinations': 'Destinations Mano River',

        'home.scope.title': 'Ce que vous allez prendre en charge (perimetre)',
        'home.scope.sendMoney': 'Envoyer de l argent (Visa / Portefeuille)',
        'home.scope.deliverMobile': 'Livrer vers Mobile Money',
        'home.scope.deliverBank': 'Livrer vers les comptes bancaires',
        'home.scope.payServices': 'Payer des biens et services',
        'home.scope.donations': 'Dons',
        'home.scope.contributions': 'Contributions',
        'home.scope.kyc': 'Verification d identite (KYC)',
        'home.scope.notifications': 'Notifications',
        'home.scope.comingSoon': 'Bientot disponible',
        'home.scope.yes': 'Oui',

        'home.section.features.title': 'Ce que vous pouvez faire',
        'home.section.features.subtitle':
            'Envoyez depuis la diaspora vers les pays de la Mano River. Livraison vers Mobile Money et banques. Vous pouvez aussi payer des biens et services.',

        'home.tag.live': 'Live',
        'home.action.useNow': 'Utiliser',

        'home.feature.wallet.title': 'Portefeuille multi-devises',
        'home.feature.wallet.desc': 'Gardez des soldes et envoyez en quelques minutes. USD, GBP, EUR, AUD (phase 1).',

        'home.feature.mobile.title': 'Envoyer vers Mobile Money',
        'home.feature.mobile.desc': 'Livraison vers les portefeuilles mobiles en Sierra Leone, au Liberia et en Guinee.',

        'home.feature.bank.title': 'Envoyer vers les banques',
        'home.feature.bank.desc': 'Envoi direct vers les comptes bancaires dans les pays pris en charge.',

        'home.feature.pay.title': 'Payer des biens et services',
        'home.feature.pay.desc': 'Payez des ecoles, des vendeurs et des prestataires depuis la diaspora.',

        'home.feature.donations.title': 'Dons',
        'home.feature.donations.desc': 'Faites un don a des causes verifiees et suivez les recus et l impact.',

        'home.feature.contrib.title': 'Contributions',
        'home.feature.contrib.desc': 'Organisez des contributions de groupe pour des familles, associations, evenements et projets.',

        'home.cta.title': 'Faites votre premier transfert',
        'home.cta.subtitle':
            'Envoyez depuis les Etats-Unis, le Royaume-Uni, le Canada, l Australie ou la France. Livraison vers Mobile Money ou comptes bancaires en Sierra Leone, au Liberia et en Guinee.',
        'home.cta.payServices': 'Payer des services',

        'home.testimonials.title': 'Temoignages',
        'home.testimonials.subtitle': 'Avis d utilisateurs qui envoient de l argent au pays.',

        'home.testimonial.1.quote': 'Rapide et clair. Ma famille a recu le Mobile Money le meme jour.',
        'home.testimonial.1.meta': 'Etats-Unis vers Sierra Leone',

        'home.testimonial.2.quote': 'Flux simple. J aime pouvoir suivre le transfert et les recus.',
        'home.testimonial.2.meta': 'Royaume-Uni vers Liberia',

        'home.testimonial.3.quote': 'L application est propre. Les frais sont faciles a comprendre.',
        'home.testimonial.3.meta': 'France vers Guinee',

        // ---------------- FOOTER ----------------
        'footer.about':
            'Transferts d argent rapides, surs et abordables vers la Sierra Leone, le Liberia et la Guinee. Pour les familles et les entreprises, au-dela des frontieres.',

        'footer.section.services': 'Services',
        'footer.section.support': 'Support',
        'footer.section.company': 'Entreprise',
        'footer.section.legal': 'Juridique',

        'footer.link.send': 'Envoyer de l argent',
        'footer.link.receive': 'Recevoir de l argent',
        'footer.link.mobile': 'Portefeuille mobile',
        'footer.link.bank': 'Transferts bancaires',

        'footer.link.help': "Centre d aide",
        'footer.link.contact': 'Nous contacter',
        'footer.link.faq': 'FAQ',
        'footer.link.status': 'Statut',

        'footer.link.about': 'A propos',
        'footer.link.careers': 'Carrieres',
        'footer.link.press': 'Presse',
        'footer.link.blog': 'Blog',

        'footer.link.privacy': 'Politique de confidentialite',
        'footer.link.terms': "Conditions d utilisation",
        'footer.link.compliance': 'Conformite',
        'footer.link.kyc': 'Politique KYC',

        'footer.rights': 'Tous droits reserves.',
        'footer.licensed': 'Emetteur de fonds agree',
        'footer.nmls': 'NMLS ID: 1234567',
        'footer.regulatory': 'Informations reglementaires',

        // ---------------- DASHBOARD ----------------
        'dashboard.welcome': 'Bon retour, {{name}} !',
        'dashboard.subtitle': 'Votre compte en un coup d oeil.',

        'dashboard.stats.totalBalance': 'Solde total',
        'dashboard.stats.totalSent': 'Total envoye',
        'dashboard.stats.totalReceived': 'Total recu',
        'dashboard.stats.transactions': 'Transactions',

        'dashboard.quickActions': 'Actions rapides',
        'dashboard.transferHistory': 'Historique des transferts',
        'dashboard.viewAll': 'Voir tout',
        'dashboard.recentTransactions': 'Transactions recentes',

        'dashboard.action.send.title': 'Envoyer de l argent',
        'dashboard.action.send.desc': 'Envoi vers Mobile Money ou banque en quelques minutes.',

        'dashboard.action.wallet.title': 'Portefeuille',
        'dashboard.action.wallet.desc': 'Ajoutez de l argent et gerez vos soldes.',

        'dashboard.action.pay.title': 'Payer biens et services',
        'dashboard.action.pay.desc': 'Payez ecoles, vendeurs et services au pays.',

        'dashboard.action.history.title': 'Historique',
        'dashboard.action.history.desc': 'Voir les transferts et les recus.',

        'dashboard.quick.payment': 'Paiement',
        'dashboard.quick.bank': 'Banque',
        'dashboard.quick.payBill': 'Payer facture',
        'dashboard.quick.donate': 'Faire un don',
        'dashboard.quick.tickets': 'Billets',
        'dashboard.quick.contribution': 'Contribution/Osusu',
        'dashboard.quick.proofCenter': 'Centre de preuves',
        'dashboard.quick.servicePros': 'Pros de service',
        'dashboard.quickActions.label': 'Actions rapides',
        'dashboard.walletBalance.label': 'Solde du portefeuille',
        'dashboard.walletBalance.today': '+$120 aujourd hui',
        'dashboard.weeklyTransfers.label': 'Transferts hebdomadaires',
        'dashboard.sampleData.label': 'Donnees de demonstration (mise en page)',
        'dashboard.recentTransactions.label': 'Transactions recentes',

        // ---------------- ENVOYER DE L ARGENT ----------------
        'send.title': 'Envoyer de l argent',
        'send.subtitle':
            'Payez avec Visa/carte ou le solde du portefeuille. Livraison vers Portefeuille mobile ou comptes bancaires en Sierra Leone, au Liberia et en Guinee.',

        'send.sendFrom': 'Envoyer depuis',
        'send.sendTo': 'Envoyer vers',

        'send.payWith': 'Payer avec',
        'send.pay.card': 'Visa / Carte',
        'send.pay.wallet': 'Solde du portefeuille',

        'send.chip.secure': 'Paiements securises',
        'send.chip.lowFees': 'Frais bas',
        'send.chip.fast': 'Livraison rapide',

        'send.deliverTo': 'Livrer vers',
        'send.deliver.mobile': 'Portefeuille mobile',
        'send.deliver.bank': 'Compte bancaire',

        'send.amount': 'Montant',
        'send.recipientName': 'Nom du beneficiaire',
        'send.recipientPhone': 'Numero Mobile Money du beneficiaire',
        'send.bankName': 'Nom de la banque',
        'send.accountNumber': 'Numero de compte',

        'send.saveRecipient': 'Enregistrer le beneficiaire',
        'send.continue': 'Continuer',

        'send.summary.title': 'Resume du transfert',
        'send.summary.payWith': 'Payer avec',
        'send.summary.deliverTo': 'Livrer vers',
        'send.summary.amount': 'Montant',
        'send.summary.fee': 'Frais de transfert',
        'send.summary.total': 'Total a payer',
        'send.summary.rate': 'Taux',
        'send.summary.receiverGets': 'Le beneficiaire recoit (estim.)',
        'send.summary.estimatesOnly':
            'Estimations uniquement. Les montants finaux dependent des taux des partenaires de paiement et des controles de conformite.',

        'send.phone.dialCode': 'Indicatif',
        'send.phone.supportedPrefixes': 'Prefixes pris en charge',
        'send.phone.supportInfo': 'Nous prenons en charge certains operateurs Mobile Money pour le moment.',
        'send.phone.supportInfo2': 'Si votre numero n est pas pris en charge, un message s affichera.',
        'send.phone.notSupported': 'Nous n envoyons pas encore vers cet operateur.',

        'send.validation.amount': 'Entrez un montant',
        'send.validation.name': 'Entrez le nom du beneficiaire',
        'send.validation.phone': 'Entrez le numero du beneficiaire',
        'send.validation.operator': 'Nous n envoyons pas encore vers cet operateur.',
        'send.validation.bankName': 'Entrez le nom de la banque',
        'send.validation.bankAccount': 'Entrez le numero de compte',

        'send.confirm.title': 'Confirmer le transfert',
        'send.confirm.recipient': 'Beneficiaire',
        'send.confirm.phone': 'Numero mobile',
        'send.confirm.bank': 'Banque',
        'send.confirm.account': 'Compte',
        'send.confirm.details': 'Details',
        'send.confirm.confirmBtn': 'Confirmer',
        'send.confirm.sending': 'Envoi...',

        'send.success': 'Transfert cree. Prochaine etape : traitement du paiement.',

        // ---------------- WALLET ----------------
        'wallet.title': 'Mon portefeuille',
        'wallet.subtitle': 'Gerez vos soldes et suivez vos transactions',
        'wallet.availableBalance': 'Solde disponible',

        'wallet.baseWallet': 'Portefeuille de base : {{currency}}',
        'wallet.addCurrency': 'Ajouter une devise',

        'wallet.addMoney': 'Ajouter de l argent',
        'wallet.withdraw': 'Retirer',
        'wallet.convert': 'Convertir',

        'wallet.recentTransactions': 'Transactions recentes',
        'wallet.tabs.all': 'Tout',
        'wallet.tabs.sent': 'Envoyees',
        'wallet.tabs.received': 'Recues',
        'wallet.tabs.pending': 'En attente',

        'wallet.monthlyActivity': 'Activite mensuelle',
        'wallet.quickStats': 'Statistiques rapides',
        'wallet.totalSent30': 'Total envoye (30 jours)',
        'wallet.totalReceived30': 'Total recu (30 jours)',
        'wallet.activeTransactions': 'Transactions actives',

        // Wallet dialogs
        'wallet.dialog.addCurrency.title': 'Ajouter une devise',
        'wallet.dialog.addCurrency.currency': 'Devise',
        'wallet.dialog.addCurrency.helper':
            'Votre portefeuille de base reste {{base}}. Vous pouvez en ajouter d autres a tout moment.',
        'wallet.dialog.addCurrency.cancel': 'Annuler',
        'wallet.dialog.addCurrency.add': 'Ajouter',

        'wallet.dialog.deposit.title': 'Ajouter de l argent au portefeuille',
        'wallet.dialog.deposit.amount': 'Montant ({{currency}})',
        'wallet.dialog.deposit.method': 'Moyen de paiement',
        'wallet.dialog.deposit.method.card': 'Carte bancaire',
        'wallet.dialog.deposit.method.bank': 'Virement bancaire',
        'wallet.dialog.deposit.method.mobile': 'Mobile Money',
        'wallet.dialog.deposit.cancel': 'Annuler',
        'wallet.dialog.deposit.confirm': 'Ajouter',

        'wallet.dialog.withdraw.title': 'Retirer du portefeuille',
        'wallet.dialog.withdraw.amount': 'Montant ({{currency}})',
        'wallet.dialog.withdraw.to': 'Retirer vers',
        'wallet.dialog.withdraw.to.bank': 'Compte bancaire',
        'wallet.dialog.withdraw.to.mobile': 'Mobile Money',
        'wallet.dialog.withdraw.cancel': 'Annuler',
        'wallet.dialog.withdraw.confirm': 'Retirer',

        // ---------------- TRANSACTIONS ----------------
        'transactions.title': 'Historique des transactions',
        'transactions.subtitle.withEmail': 'Suivez et gerez les transactions pour {{email}}',
        'transactions.subtitle.noEmail': 'Suivez et gerez toutes vos activites financieres',

        'transactions.stats.totalSent': 'Total envoye',
        'transactions.stats.totalReceived': 'Total recu',
        'transactions.stats.pending': 'En attente',
        'transactions.stats.totalFees': 'Total des frais',
        'transactions.stats.oneTransaction': '1 transaction',
        'transactions.stats.averagePct': 'Moyenne {{pct}}',

        'transactions.monthlyActivity': 'Activite mensuelle',

        'transactions.search.placeholder': 'Rechercher des transactions...',
        'transactions.filters.status': 'Statut',
        'transactions.filters.type': 'Type',
        'transactions.filters.dateRange': 'Periode',

        'transactions.filters.status.all': 'Tous les statuts',
        'transactions.filters.status.completed': 'Termine',
        'transactions.filters.status.pending': 'En attente',
        'transactions.filters.status.failed': 'Echec',

        'transactions.filters.type.all': 'Tous les types',
        'transactions.filters.type.send': 'Envoi',
        'transactions.filters.type.receive': 'Reception',
        'transactions.filters.type.conversion': 'Conversion',

        'transactions.filters.date.all': 'Toute periode',
        'transactions.filters.date.today': 'Aujourd hui',
        'transactions.filters.date.week': 'Cette semaine',
        'transactions.filters.date.month': 'Ce mois',

        'transactions.showing': 'Affichage de {{shown}} sur {{total}} transactions',

        // Table headers
        'transactions.table.id': 'ID transaction',
        'transactions.table.type': 'Type',
        'transactions.table.description': 'Description',
        'transactions.table.amount': 'Montant',
        'transactions.table.date': 'Date',
        'transactions.table.status': 'Statut',
        'transactions.table.actions': 'Actions',
        'transactions.table.fee': 'Frais : {{fee}}',

        'transactions.export': 'Exporter les transactions',
        'transactions.export.soon': 'Export bientot disponible',

        // Receipt modal
        'transactions.receipt.title': 'Recu',
        'transactions.receipt.receiptId': 'ID du recu',
        'transactions.receipt.date': 'Date',
        'transactions.receipt.status': 'Statut',
        'transactions.receipt.method': 'Methode',
        'transactions.receipt.amount': 'Montant',
        'transactions.receipt.fee': 'Frais',
        'transactions.receipt.recipient': 'Beneficiaire',
        'transactions.receipt.close': 'Fermer',
        'transactions.receipt.download': 'Telecharger',

        // ---------------- FACTURES ----------------
        'invoices.title': 'Factures',
        'invoices.subtitle': 'Creez et suivez les factures de votre entreprise.',
        'invoices.create': 'Creer une facture',
        'invoices.onlyBusiness': 'Les factures sont reservees aux comptes entreprise.',
        'invoices.loading': 'Chargement des factures...',
        'invoices.emptyTitle': 'Aucune facture pour le moment.',
        'invoices.emptySubtitle': 'Cliquez sur "Creer une facture" pour commencer.',
        'invoices.table.invoice': 'Facture',
        'invoices.table.customer': 'Client',
        'invoices.table.total': 'Total',
        'invoices.table.due': 'Echeance',
        'invoices.table.status': 'Statut',
        'invoices.table.actions': 'Actions',
        'invoices.actions.copy': 'Copier le lien de paiement',
        'invoices.actions.open': 'Ouvrir le lien de paiement',
        'invoices.actions.copied': 'Copie',
        'invoices.actions.noLink': 'Aucun lien',
        'invoices.errors.loadFailed': 'Impossible de charger les factures.',
        'invoices.status.paid': 'Payee',
        'invoices.status.sent': 'Envoyee',
        'invoices.status.overdue': 'En retard',
        'invoices.status.draft': 'Brouillon',

        // ---------------- CREER UNE FACTURE ----------------
        'invoiceCreate.title': 'Creer une facture',
        'invoiceCreate.subtitle': 'Envoyez un lien de paiement a votre client.',
        'invoiceCreate.onlyBusiness': 'Seuls les comptes entreprise peuvent creer des factures.',
        'invoiceCreate.customerName': 'Nom du client',
        'invoiceCreate.customerEmail': 'E-mail du client',
        'invoiceCreate.customerPhone': 'Telephone du client',
        'invoiceCreate.currency': 'Devise',
        'invoiceCreate.locked': 'Verrouillee',
        'invoiceCreate.currencyLockedHelp': 'La devise est verrouillee selon le pays de l entreprise.',
        'invoiceCreate.dueDate': 'Date d echeance',
        'invoiceCreate.items': 'Articles',
        'invoiceCreate.description': 'Description',
        'invoiceCreate.qty': 'Qte',
        'invoiceCreate.unitPrice': 'Prix unitaire',
        'invoiceCreate.remove': 'Retirer',
        'invoiceCreate.addItem': 'Ajouter un article',
        'invoiceCreate.note': 'Note (facultatif)',
        'invoiceCreate.total': 'Total',
        'invoiceCreate.create': 'Creer la facture',
        'invoiceCreate.creating': 'Creation...',
        'invoiceCreate.errors.customerNameRequired': 'Le nom du client est requis.',
        'invoiceCreate.errors.dueDateRequired': 'La date d echeance est requise.',
        'invoiceCreate.errors.addItemAmount': 'Ajoutez au moins un article avec un montant.',
        'invoiceCreate.errors.createFailed': 'Impossible de creer la facture.',

        // ---------------- PUBLIC INVOICE PAY (PAYER) ----------------
        'publicInvoice.title': 'Payer une facture',
        'publicInvoice.subtitle': 'Verifiez les details et payez en toute securite.',
        'publicInvoice.loading': 'Chargement de la facture...',
        'publicInvoice.notFound': 'Facture introuvable.',
        'publicInvoice.goHome': 'Accueil',

        // labels (these are the ones still showing English in your screenshot)
        'publicInvoice.invoiceLabel': 'Facture',
        'publicInvoice.customerLabel': 'Client',
        'publicInvoice.dueLabel': 'Echeance',

        'publicInvoice.amountDue': 'Montant du',
        'publicInvoice.youPay': 'Vous payez',
        'publicInvoice.yourCurrency': 'Votre devise',

        'publicInvoice.exchangeRate': 'Taux de change',
        'publicInvoice.feeEstimate': 'Frais estimes',
        'publicInvoice.total': 'Total',
        'publicInvoice.theyReceive': 'Ils recoivent',

        'publicInvoice.items': 'Articles',
        'publicInvoice.itemFallback': 'Article',
        'publicInvoice.note': 'Note',

        'publicInvoice.back': 'Retour',
        'publicInvoice.startingPayment': 'Demarrage du paiement...',
        'publicInvoice.payNow': 'Payer maintenant',
        'publicInvoice.missingPayUrl': 'Configuration de paiement manquante (aucune URL retournee).',
        'publicInvoice.startPayFailed': 'Impossible de demarrer le paiement.',
    },
}

const interpolate = (str, vars) => {
    if (!vars || typeof vars !== 'object') return str
    return String(str).replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, k) => {
        const key = String(k).trim()
        const v = vars[key]
        return v === undefined || v === null ? '' : String(v)
    })
}


export const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState('en')

    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved && (saved === 'en' || saved === 'fr')) setLang(saved)
    }, [])

    const setLanguage = (next) => {
        const safe = next === 'fr' ? 'fr' : 'en'
        setLang(safe)
        localStorage.setItem(STORAGE_KEY, safe)
    }


    // t(key, vars, "Fallback")
    const t = (key, vars = null, fallback = '') => {
        const raw = translations[lang]?.[key]
        const val = raw === undefined || raw === null || raw === '' ? fallback || key : raw
        return interpolate(val, vars)
    }

    const value = useMemo(() => ({ lang, setLanguage, t }), [lang])

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => useContext(LanguageContext)

